import React, { useState } from 'react';
import { Card } from '../../../../../components/Card/Card';
import { t } from '../../../../../locales/translate';
import { Button } from '../../../../../components/Button/Button';
import { Order } from '../../../../../model/order';
import { ControlledModal } from '../../../../../components/Modal/ControlledModal';
import { RefundEmail } from '../../modals/RefundEmail';
import { useRemoteData } from '../../../../../model/data';
import { getRawEvent, getProductRecall } from '../../../../../model/event';
import { Spinner } from '../../../../../components/Spinner/Spinner';
import { useErrorHandler, allPromisesSettled } from '../../../../../util/util';
import { compact } from 'lodash';

interface Props {
  order: Order;
}

export const TicketRefundTool = ({ order }: Props): JSX.Element | null => {
  const [displayModal, setDisplayModal] = useState(false);

  const eventIds = compact(
    order.itemGroups?.filter((item) => item.product?.__typename === 'Event').flatMap((item) => item.product?.id) || []
  );

  const [events, eventsLoading, eventsError] = useRemoteData(
    async () => {
      const eventPromises = eventIds.map((id) => getRawEvent(id));
      await Promise.all(eventPromises);
      const recallPromises = eventIds.map((id) => getProductRecall(id));
      await allPromisesSettled(recallPromises);
    },
    (db) =>
      db
        .table('events')
        .where((event) => eventIds.includes(event.id))
        .filter((event) => event.recallDetails?.id && !event.recallDetails?.paused)
  );

  useErrorHandler(eventsError);

  if (events.length === 0) {
    return null;
  }

  return (
    <Card title={t.model.event.massRefund.ticketRefundTool}>
      {eventsLoading ? (
        <Spinner color="var(--color-tint)" />
      ) : (
        <Button
          disabled={events.length === 0}
          className="ticket-refund__button"
          onClick={() => setDisplayModal(true)}
          type="primary"
          title={t.general.open}
        />
      )}

      <ControlledModal<{}>
        open={displayModal}
        model={{}}
        onClose={() => setDisplayModal(false)}
        title={t.model.event.massRefund.sendRefundEmail}
        size="small"
        saveTitleOverride={t.actions.send('')}
      >
        {(controller) => (
          <RefundEmail orderId={order.id} email={order.user?.email} events={events} controller={controller} />
        )}
      </ControlledModal>
    </Card>
  );
};
